// ================================================================
//  BmelEditorService
//
//  Responsibilities:
//    1. Load Monaco from CDN (lazy, once)
//    2. Register the BMEL language with syntax highlighting and autocomplete
//    3. Validate expressions in real time
//    4. Expose openEditor(config) → Promise<string>
//    5. Fetch BMEL function registry from the backend API
//
//  Data model:
//    - A BizEvent mapping has N source fields → 1 target component
//    - The editor opens for a TARGET COMPONENT
//    - BMEL references to source fields use the source.id with $ prefix:
//        $op0_REQUEST_FIELD_$.firstName
//        $op0_RESPONSE_FIELD_$.data.id
//        $op0_HEADER_Authorization
//        $op0_QUERY_PARAM_page
//        $op0_URI_PARAM_userId
//
//  Usage from the directive:
//    BmelEditorService.openEditor({
//        targetComponentId:   compId,
//        targetComponentName: compName,
//        currentExpression:   ctrl.targetExpressions[compId] || '',
//        availableSources:    allSources
//    }).then(function(expression) {
//        ctrl.targetExpressions[compId] = expression;
//    });
// ================================================================

angular.module('bizmetryApp')

    .service('BmelEditorService', ['$q', '$http', '$mdDialog', 'CONFIG', function ($q, $http, $mdDialog, CONFIG) {

        var svc = this;

        var _monacoPromise         = null;
        var _completionDisposable  = null;
        var _currentSources        = [];
        var _currentEnumValues     = [];   // enum constants for the active editor
        var _currentResourceTypes  = [];   // template resource types for #{...} (see normalizeResourceTypes)

        // ----------------------------------------------------------------
        //  Public API
        // ----------------------------------------------------------------

        svc.loadMonaco                = loadMonaco;
        svc.registerCompletions       = registerCompletions;
        svc.refreshLanguageTokenizer  = refreshLanguageTokenizer;
        svc.validateExpression      = validateExpression;
        svc.frameTypeToExpectedBmel = frameTypeToExpectedBmel;
        svc.bmelTypeLabel           = bmelTypeLabel;
        svc.openEditor          = openEditor;
        svc.setFunctions             = setFunctions;
        svc.getFunctions             = function () { return BMEL_FUNCTIONS; };
        svc.getFunctionGroups        = function () { return BMEL_FUNCTION_GROUPS; };
        svc.loadFunctions            = loadFunctions;
        svc.loadFunctionById         = loadFunctionById;
        svc.buildDefaultExpression   = buildDefaultExpression;
        svc.buildResourceTypes       = buildResourceTypes;

        // ----------------------------------------------------------------
        //  BMEL function catalogue
        //
        //  BMEL_FUNCTIONS   — flat array; used by Monaco completions,
        //                     syntax highlighting, and expression validator.
        //                     Shape: { name, sig, desc, snippet, arguments[] }
        //
        //  BMEL_FUNCTION_GROUPS — array grouped by category; used by the
        //                         editor panel to render grouped lists.
        //                         Shape: { category, label, icon, functions[] }
        // ----------------------------------------------------------------

        var BMEL_FUNCTIONS       = [];
        var BMEL_FUNCTION_GROUPS = [];
        var KNOWN_FN_NAMES       = [];

        // Category display metadata — maps the enum name from the API to
        // a human-readable label and a Material Icon identifier.
        // Keys must match the category strings returned by the backend registry.
        var _CATEGORY_META = {
            'ARRAY':  { label: 'Array',  icon: 'view_list'   },
            'DATE':   { label: 'Date',   icon: 'event'        },
            'JSON':   { label: 'JSON',   icon: 'data_object'  },
            'LLM':    { label: 'LLM',    icon: 'psychology'   },
            'LOGIC':  { label: 'Logic',  icon: 'rule'         },
            'MATH':   { label: 'Math',   icon: 'calculate'    },
            'SCALAR': { label: 'Scalar', icon: 'onetwothree'  },
            'STRING': { label: 'String', icon: 'text_fields'  }
        };

        // ----------------------------------------------------------------
        //  API — GET /v1/api/bizmetry-bmel/functions
        //
        //  The backend now returns a Map<String, List<BmelFunctionResponse>>:
        //    { "ARRAY": [...], "DATE": [...], "LLM": [...], ... }
        //  Keys are in ascending lexicographic order (server-side TreeMap).
        //  Functions within each group are ordered by name ASC.
        // ----------------------------------------------------------------

        function loadFunctions(nameFilter, descriptionFilter) {
            var params = {};
            if (nameFilter)        params.name        = nameFilter;
            if (descriptionFilter) params.description = descriptionFilter;

            return $http.get(CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-bmel/functions', {
                params: params,
                headers: { 'Content-Type': 'application/json' }
            }).then(function (response) {
                _populateFunctions(response.data || {});
                return BMEL_FUNCTIONS;
            });
        }

        // ----------------------------------------------------------------
        //  API — GET /v1/api/bizmetry-bmel/functions/{functionId}
        // ----------------------------------------------------------------

        function loadFunctionById(functionId) {
            return $http.get(
                CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-bmel/functions/' + functionId,
                { headers: { 'Content-Type': 'application/json' } }
            ).then(function (response) {
                return response.data;
            });
        }

        // ----------------------------------------------------------------
        //  Internal — map grouped BmelFunctionResponse map → internal catalogues.
        //
        //  Populates two structures from the same pass:
        //    • BMEL_FUNCTIONS        — flat, for Monaco and validator
        //    • BMEL_FUNCTION_GROUPS  — grouped, for the editor panel
        //
        //  Object.keys() preserves insertion order in all supported browsers
        //  (V8 / SpiderMonkey) for string keys — so the server's lexicographic
        //  order is maintained without an explicit sort here.
        // ----------------------------------------------------------------

        function _populateFunctions(grouped) {
            BMEL_FUNCTION_GROUPS = [];
            BMEL_FUNCTIONS       = [];

            Object.keys(grouped || {}).forEach(function (category) {
                var items = grouped[category] || [];

                var meta = _CATEGORY_META[category] || { label: category, icon: 'functions' };

                var fns = items.map(function (item) {
                    return {
                        name:          item.name,
                        sig:           item.signature,
                        desc:          item.description,
                        returnType:    item.returnType || 'bmel:any',
                        snippet:       _buildSnippet(item.name, item.arguments),
                        category:      category,
                        categoryLabel: meta.label,
                        arguments:     (item.arguments || [])
                                           .slice()
                                           .sort(function (a, b) { return a.position - b.position; })
                    };
                });

                BMEL_FUNCTIONS = BMEL_FUNCTIONS.concat(fns);

                var meta = _CATEGORY_META[category] || { label: category, icon: 'functions' };
                BMEL_FUNCTION_GROUPS.push({
                    category:  category,
                    label:     meta.label,
                    icon:      meta.icon,
                    functions: fns
                });
            });

            KNOWN_FN_NAMES = BMEL_FUNCTIONS.map(function (f) { return f.name; });
        }

        /**
         * Builds a Monaco snippet string from the argument list.
         * Each argument becomes a tabstop: ${1:argName}, ${2:argName}, ...
         * Variadic arguments get a "..." suffix on their tabstop label.
         */
        function _buildSnippet(name, args) {
            if (!args || args.length === 0) return name + '()';
            var params = args
                .slice()
                .sort(function (a, b) { return a.position - b.position; })
                .map(function (arg, idx) {
                    var label = arg.variadic ? arg.name + '...' : arg.name;
                    if (_symbolParamType(arg.type)) {
                        return '#{${' + (idx + 1) + ':' + _stripBmel(arg.name) + '}}';
                    }
                    return '${' + (idx + 1) + ':' + label + '}';
                })
                .join(', ');
            return name + '(' + params + ')';
        }

        /**
         * Legacy — populate catalogue from refdata API format.
         * Kept for backward compatibility; prefer loadFunctions() for new code.
         */
        function setFunctions(refItems) {
            var fns = (refItems || [])
                .map(function (item) {
                    var raw   = item.refObjectDesc || '';
                    var parts = raw.split(' — ');
                    var name  = item.refObjectLabel || '';
                    return {
                        name:      name,
                        sig:       parts[0] || name,
                        desc:      parts[1] || '',
                        snippet:   name + '(${1})',
                        arguments: []
                    };
                })
                .sort(function (a, b) { return a.name.localeCompare(b.name); });

            // Wrap in a synthetic grouped structure so both catalogues stay in sync
            var grouped = {};
            fns.forEach(function (fn) {
                var cat = 'OTHER';
                grouped[cat] = grouped[cat] || [];
                grouped[cat].push({
                    name: fn.name, signature: fn.sig, description: fn.desc,
                    returnType: 'bmel:any', arguments: []
                });
            });
            _populateFunctions(grouped);
        }


        // ----------------------------------------------------------------
        //  Default expression builder
        //
        //  Called when connecting a source field to a target component for
        //  the first time (i.e. no expression has been set yet).
        //
        //  Rules:
        //    a) Composite source + target ≠ NESTED_TABLE → bmel:flatten(ref)
        //    b) Composite source + target = NESTED_TABLE → bmel:self(ref)
        //    c) Scalar source             → any target   → bmel:self(ref)
        //
        //  "Composite" means: valueType starts with 'bmel:array' (arrays) OR
        //  equals 'bmel:any' (objects normalise to bmel:any via _normSrcType).
        // ----------------------------------------------------------------

        /**
         * Returns the default BMEL expression for a source → component binding.
         *
         * @param {Object} source          - entry from availableSources
         *                                   must have { ref: string, valueType: string }
         * @param {string} targetFrameType - frame type of the target component,
         *                                   e.g. 'NESTED_TABLE', 'FIELD', 'TEXT', ...
         * @returns {string} e.g. 'bmel:self({op:Section}.$.field)'
         *                     or 'bmel:flatten({op:Section}.$.items)'
         */
        function buildDefaultExpression(source, targetFrameType) {
            var ref = source.ref       || '';
            var vt  = _normSrcType(source.valueType || '');    // normalise raw swagger → bmel:xxx

            var isComposite = vt.indexOf('bmel:array') === 0   // array or array<T>
                           || vt === 'bmel:any';               // object normalises to bmel:any

            var fn = (isComposite && targetFrameType !== 'NESTED_TABLE')
                ? 'bmel:flatten'   // rule a
                : 'bmel:self';     // rules b + c

            return fn + '(' + ref + ')';
        }

        // ----------------------------------------------------------------
        //  1. Lazy-load Monaco from CDN
        // ----------------------------------------------------------------

        function loadMonaco() {
            if (_monacoPromise) return _monacoPromise;

            _monacoPromise = $q(function (resolve, reject) {
                if (window.monaco) { _registerLanguage(); resolve(window.monaco); return; }

                var script    = document.createElement('script');
                script.src    = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.44.0/min/vs/loader.js';
                script.onload = function () {
                    window.require.config({ paths: { vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.44.0/min/vs' } });
                    window.require(['vs/editor/editor.main'], function () { _registerLanguage(); resolve(window.monaco); });
                };
                script.onerror = function () { reject(new Error('[BmelEditorService] CDN load failed')); };
                document.head.appendChild(script);
            });

            return _monacoPromise;
        }

        // ----------------------------------------------------------------
        //  2. BMEL language — tokenizer + dark theme
        // ----------------------------------------------------------------

        function _registerLanguage() {
            if (monaco.languages.getLanguages().some(function (l) { return l.id === 'bmel'; })) return;

            monaco.languages.register({ id: 'bmel', aliases: ['BMEL'] });

            monaco.languages.setMonarchTokensProvider('bmel', {
                tokenizer: {
                    root: [
                        [/#\{[^}\n]*\}?/, 'bmel.symbol'],
                        [/#enum\.[a-zA-Z_][a-zA-Z0-9_]*/, 'bmel.enum'],
                        [new RegExp('\\b(' + KNOWN_FN_NAMES.join('|') + ')(?=\\s*\\()'), 'bmel.fn'],
                        [/\b(true|false|null)\b/, 'bmel.const'],
                        [/\{[^}]+\}\.\$(?:\.[a-zA-Z0-9_.\[\]-]*)?/, 'bmel.ref'],
                        [/"([^"\\]|\\.)*"/, 'bmel.str'],
                        [/'([^'\\]|\\.)*'/, 'bmel.str'],
                        [/\d+(\.\d+)?/, 'bmel.num'],
                        [/[()[\]]/, '@brackets'],
                        [/[=><!]=?|[<>]/, 'bmel.op'],
                        [/&&|\|\|/, 'bmel.op'],
                        [/[+\-*\/]/, 'bmel.op'],
                        [/[.,;]/, 'delimiter'],
                    ]
                }
            });

            monaco.editor.defineTheme('bmel-dark', {
                base: 'vs-dark', inherit: true,
                rules: [
                    { token: 'bmel.fn',    foreground: 'DCDCAA' },
                    { token: 'bmel.const', foreground: '569CD6' },
                    { token: 'bmel.ref',   foreground: '4EC9B0' },
                    { token: 'bmel.str',   foreground: 'CE9178' },
                    { token: 'bmel.num',   foreground: 'B5CEA8' },
                    { token: 'bmel.op',    foreground: 'D4D4D4' },
                    { token: 'bmel.enum',  foreground: 'FFD700' },
                    { token: 'bmel.symbol', foreground: 'C586C0' },
                ],
                colors: {
                    'editor.background':               '#1A1A2E',
                    'editor.foreground':               '#D4D4D4',
                    'editor.lineHighlightBackground':  '#2D2D44',
                    'editorLineNumber.foreground':     '#555577',
                    'editor.selectionBackground':      '#264F78',
                    'editorCursor.foreground':         '#A78BFA',
                    'editorSuggestWidget.background':  '#1E1E3A',
                    'editorSuggestWidget.border':      '#6366F1',
                    'editorSuggestWidget.selectedBackground': '#3730A3',
                }
            });
        }

        // ----------------------------------------------------------------
        //  2b. Refresh tokenizer — call after KNOWN_FN_NAMES is populated
        //
        //  Monaco caches the Monarch tokenizer when the language is first
        //  registered. If KNOWN_FN_NAMES was empty at that point (because
        //  loadFunctions() hadn't resolved yet), function names won't be
        //  highlighted or validated correctly on the first editor open.
        //  This function updates the tokenizer with the current KNOWN_FN_NAMES.
        // ----------------------------------------------------------------

        function refreshLanguageTokenizer() {
            if (!window.monaco) { return; }
            // Re-set the Monarch tokenizer with the now-populated function list
            monaco.languages.setMonarchTokensProvider('bmel', {
                tokenizer: {
                    root: [
                        [/#\{[^}\n]*\}?/, 'bmel.symbol'],
                        [/#enum\.[a-zA-Z_][a-zA-Z0-9_]*/, 'bmel.enum'],
                        [new RegExp('\\b(' + (KNOWN_FN_NAMES.length ? KNOWN_FN_NAMES.join('|') : 'bmel__noop') + ')(?=\\s*\\()'), 'bmel.fn'],
                        [/\b(true|false|null)\b/, 'bmel.const'],
                        [/\{[^}]+\}\.\$(?:\.[a-zA-Z0-9_.[\]-]*)?/, 'bmel.ref'],
                        [/"([^"\\]|\\.)*"/, 'bmel.str'],
                        [/'([^'\\]|\\.)*'/, 'bmel.str'],
                        [/\d+(\.\d+)?/, 'bmel.num'],
                        [/[()[\]]/, '@brackets'],
                        [/[=><!]=?|[<>]/, 'bmel.op'],
                        [/&&|\|\|/, 'bmel.op'],
                        [/[+\-*\/]/, 'bmel.op'],
                        [/[.,;]/, 'delimiter'],
                    ]
                }
            });
        }

        // ----------------------------------------------------------------
        //  3. Autocomplete
        // ----------------------------------------------------------------

        function registerCompletions(availableSources, enumValues, resourceTypes) {
            _currentSources       = availableSources || [];
            _currentEnumValues    = enumValues       || [];
            _currentResourceTypes = resourceTypes    || [];
            if (_completionDisposable) return;

            _completionDisposable = monaco.languages.registerCompletionItemProvider('bmel', {
                triggerCharacters: ['{', '(', '#'],

                provideCompletionItems: function (model, position) {
                    var textBefore = model.getValueInRange({
                        startLineNumber: 1, startColumn: 1,
                        endLineNumber: position.lineNumber, endColumn: position.column
                    });
                    var word = model.getWordUntilPosition(position);
                    var range = {
                        startLineNumber: position.lineNumber, endLineNumber: position.lineNumber,
                        startColumn: word.startColumn, endColumn: position.column
                    };
                    var suggestions = [];

                    // #{Name} — resource type / JSON field reference. Typing "#{" opens the
                    // list; Monaco filters it as the name is typed (inline search).
                    var _symM = /#\{[^}\n]*$/.exec(textBefore);
                    if (_symM) {
                        return { suggestions: _symbolSuggestions(model, position, textBefore.length - _symM[0].length, textBefore) };
                    }

                    // #enum.VALUE completions — fires when user types '#'
                    var _hashM = /#[a-zA-Z0-9_.]*$/.exec(textBefore);
                    if (_hashM) {
                        var _lnStart   = textBefore.lastIndexOf('\n') + 1;
                        var _hStart    = textBefore.length - _hashM[0].length;
                        var _enumRange = {
                            startLineNumber: position.lineNumber, endLineNumber: position.lineNumber,
                            startColumn: _hStart - _lnStart + 1, endColumn: position.column
                        };
                        _currentEnumValues.forEach(function (val) {
                            suggestions.push({
                                label:         '#enum.' + val,
                                kind:          monaco.languages.CompletionItemKind.EnumMember,
                                detail:        'bmel:enum',
                                documentation: 'Enum constant: ' + val,
                                insertText:    '#enum.' + val,
                                range:         _enumRange
                            });
                        });
                        if (_hashM[0] === '#') {
                            suggestions = suggestions.concat(_symbolSuggestions(model, position, _hStart, textBefore));
                        }
                        if (suggestions.length) return { suggestions: suggestions };
                    }

                    if (/\{[^}]*$/.test(textBefore)) {
                        var currentLineStart = textBefore.lastIndexOf('\n') + 1;
                        var bracePos         = textBefore.lastIndexOf('{');
                        var replaceRange = {
                            startLineNumber: position.lineNumber,
                            endLineNumber:   position.lineNumber,
                            startColumn:     bracePos - currentLineStart + 1,
                            endColumn:       position.column
                        };
                        _currentSources.forEach(function (src) {
                            suggestions.push({
                                label:         src.ref,
                                kind:          monaco.languages.CompletionItemKind.Variable,
                                detail:        src.valueType + '  [' + src.sourceType + ']',
                                documentation: src.opName || '',
                                insertText:    src.ref,
                                range:         replaceRange
                            });
                        });
                    } else if (!textBefore.trim() || /[\s,(=!<>+\-*\/]$/.test(textBefore)) {
                        suggestions.push({
                            label:      '{',
                            kind:       monaco.languages.CompletionItemKind.Keyword,
                            detail:     'Reference a source field',
                            insertText: '{',
                            range:      range
                        });
                        BMEL_FUNCTIONS.forEach(function (fn) {
                            suggestions.push({
                                label:           fn.name,
                                kind:            monaco.languages.CompletionItemKind.Function,
                                detail:          fn.sig,
                                documentation:   fn.desc,
                                insertText:      fn.snippet,
                                insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                                range:           range
                            });
                        });
                    }
                    return { suggestions: suggestions };
                }
            });
        }

        // ----------------------------------------------------------------
        //  3b. #{...} references — resource types and JSON fields
        //
        //  #{AIMODEL} names a resource type of the template the target Frame
        //  Type belongs to (bmel:resource-type arguments); #{COST_PER_TOKEN}
        //  names a field of a JSON object (bmel:json-field arguments). For a
        //  resource returned by bmel:get-resource-by-id the fields are the
        //  imported columns of its resource type.
        //
        //  Resource type shape used by the editor:
        //    { id, name, description, imported, fields: [{ name, label, type }] }
        // ----------------------------------------------------------------

        var _SYMBOL_PARAM_TYPES = ['bmel:resource-type', 'bmel:json-field'];

        /** Template config (templateConfig or the template itself) → editor resource types. */
        function buildResourceTypes(templateFull) {
            var config = (templateFull && (templateFull.templateConfig || templateFull)) || {};
            var attrs  = config.attribute_types || [];
            return (config.resource_types || []).filter(function (rt) {
                return rt && rt.name && rt.enabled !== false;
            }).map(function (rt) {
                return {
                    id:          rt.id,
                    name:        rt.name,
                    description: rt.description || '',
                    imported:    !!rt.is_resource_imported,
                    fields: attrs.filter(function (at) {
                        return at && String(at.resource_id) === String(rt.id) && at.enabled !== false
                            && (at.column_metadata_name || at.name);
                    }).map(function (at) {
                        return {
                            name:  at.column_metadata_name || at.name,
                            label: at.name || at.column_metadata_name,
                            type:  at.scalar_attribute_type || null
                        };
                    })
                };
            });
        }

        function _symbolParamType(t) {
            var parts = String(t || '').split('|').map(function (x) { return x.trim(); });
            return _SYMBOL_PARAM_TYPES.filter(function (st) { return parts.indexOf(st) !== -1; })[0] || null;
        }

        function _findByName(list, name) {
            var exact = (list || []).filter(function (x) { return x.name === name; })[0];
            if (exact) return { item: exact };
            var lower = String(name).toLowerCase();
            var near  = (list || []).filter(function (x) { return String(x.name).toLowerCase() === lower; })[0];
            return { item: null, hint: near ? near.name : null };
        }

        // Value type of a resource field — imported JSON attributes stay bmel:any.
        function _fieldValueType(field) {
            if (!field || !field.type) return 'bmel:any';
            var t = String(field.type).toLowerCase();
            if (t === 'json' || t === 'object') return 'bmel:any';
            return _normSrcType(field.type);
        }

        /**
         * Innermost function call open at the end of `text` (strings, {…} and #{…}
         * references skipped): { fn, argIndex, args: [text of completed args] } or null.
         */
        function _enclosingCall(text) {
            var stack = [], i = 0, n = text.length;
            while (i < n) {
                var ch = text[i];
                if (ch === '"' || ch === "'") {
                    var j = i + 1;
                    while (j < n && text[j] !== ch) { j += text[j] === '\\' ? 2 : 1; }
                    i = j + 1; continue;
                }
                if (ch === '{' || (ch === '#' && text[i + 1] === '{')) {
                    var close = text.indexOf('}', i);
                    if (close === -1) break;
                    i = close + 1; continue;
                }
                if (ch === '(') {
                    var m = /(bmel:[a-zA-Z_][a-zA-Z0-9_-]*|[a-zA-Z_][a-zA-Z0-9_]*)\s*$/.exec(text.substring(0, i));
                    stack.push({ fn: m ? m[1] : null, argIndex: 0, argStart: i + 1, args: [] });
                } else if (ch === ',' && stack.length) {
                    var top = stack[stack.length - 1];
                    top.args.push(text.substring(top.argStart, i));
                    top.argIndex++;
                    top.argStart = i + 1;
                } else if (ch === ')') {
                    stack.pop();
                }
                i++;
            }
            return stack.length ? stack[stack.length - 1] : null;
        }

        /** Fields offered for a json-field argument: the resource type of the object argument, else all. */
        function _fieldsForCall(call) {
            var objText = call && call.args.length ? call.args[0] : '';
            var m = /bmel:get-resource-by-id\s*\(\s*#\{([^}]*)\}/.exec(objText || '');
            var rt = m ? _findByName(_currentResourceTypes, m[1].trim()).item : null;
            var types = rt ? [rt] : _currentResourceTypes;
            var seen = {}, out = [];
            types.forEach(function (t) {
                (t.fields || []).forEach(function (f) {
                    if (seen[f.name]) return;
                    seen[f.name] = true;
                    out.push({ field: f, owner: t });
                });
            });
            return out;
        }

        // Suggestions for a #{…} reference starting at offset `hashOffset` of `textBefore`.
        function _symbolSuggestions(model, position, hashOffset, textBefore) {
            var call   = _enclosingCall(textBefore.substring(0, hashOffset));
            var fnDef  = call ? BMEL_FUNCTIONS.filter(function (f) { return f.name === call.fn; })[0] : null;
            var param  = fnDef && fnDef.arguments ? fnDef.arguments[call.argIndex] : null;
            var kind   = _symbolParamType(param && param.type) || 'bmel:resource-type';

            var lineStart = textBefore.lastIndexOf('\n') + 1;
            var lineText  = model.getLineContent(position.lineNumber);
            var endColumn = lineText.charAt(position.column - 1) === '}' ? position.column + 1 : position.column;
            var range = {
                startLineNumber: position.lineNumber, endLineNumber: position.lineNumber,
                startColumn: hashOffset - lineStart + 1, endColumn: endColumn
            };

            if (kind === 'bmel:json-field') {
                return _fieldsForCall(call).map(function (entry, idx) {
                    var f = entry.field;
                    return {
                        label:         { label: f.name, description: entry.owner.name },
                        kind:          monaco.languages.CompletionItemKind.Field,
                        detail:        'JSON field · ' + (f.type || 'any') + ' · ' + entry.owner.name,
                        documentation: f.label && f.label !== f.name ? f.label : '',
                        insertText:    '#{' + f.name + '}',
                        filterText:    '#{' + f.name,
                        sortText:      ('0000' + idx).slice(-5),
                        range:         range
                    };
                });
            }
            return _currentResourceTypes.map(function (rt, idx) {
                return {
                    label:         { label: rt.name, description: rt.imported ? 'imported' : '' },
                    kind:          monaco.languages.CompletionItemKind.Class,
                    detail:        'Resource type' + (rt.imported ? ' · imported from DataSource' : ''),
                    documentation: rt.description || '',
                    insertText:    '#{' + rt.name + '}',
                    filterText:    '#{' + rt.name,
                    sortText:      ('0000' + idx).slice(-5),
                    range:         range
                };
            });
        }

        // ----------------------------------------------------------------
        //  4. Expression validation
        // ----------------------------------------------------------------

        // All valid BMEL functions are in the registry with the bmel: prefix.
        // Short names (trim, concat, etc.) are NOT valid — only bmel:trim,
        // bmel:concat, etc. are accepted. Keeping this empty ensures the
        // syntax checker rejects unprefixed calls like trim(15).
        var _BUILTIN_FN_NAMES = [];

        // Arity map is kept for reference but only applies to registry-looked-up
        // functions; the registry's own arguments array drives validation now.
        var _FN_ARITY = {};

        function _tokenize(src) {
            var tokens = [];
            var i = 0, n = src.length;

            function mkErr(msg, col, len) {
                return { message: msg, line: 1, col: col, length: len || 1, severity: 'error' };
            }

            while (i < n) {
                while (i < n && /[ \t\r\n]/.test(src[i])) i++;
                if (i >= n) break;
                var col = i + 1;

                if (src[i] === '{') {
                    var close = src.indexOf('}', i + 1);
                    if (close === -1) return { error: mkErr('Unclosed "{" in field reference', col) };
                    var inner    = src.substring(i + 1, close);
                    var colonIdx = inner.indexOf(':');
                    if (colonIdx === -1) return { error: mkErr('Field reference missing ":" — expected {Operation:Section}', col, close - i + 1) };
                    var opName      = inner.substring(0, colonIdx);
                    var sectionName = inner.substring(colonIdx + 1);
                    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(opName.trim())) return { error: mkErr('Invalid operation name "' + opName.trim() + '" in field reference', col, close - i + 1) };
                    if (!sectionName.trim()) return { error: mkErr('Missing section name in field reference', col, close - i + 1) };
                    // Operation-level array ref of a MANY-cardinality operation —
                    // {Op:Invocations[]}.$ — addresses the N invocations themselves
                    // (one element per invocation), so it carries no field path.
                    if (/\[\]\s*$/.test(sectionName) && src.substring(close + 1, close + 3) === '.$'
                            && src.charAt(close + 3) !== '.') {
                        tokens.push({ type: 'FIELD_REF', value: src.substring(i, close + 3), col: col });
                        i = close + 3;
                        continue;
                    }
                    if (src.substring(close + 1, close + 4) !== '.$.') return { error: mkErr('Field reference must be followed by ".$."', col, close - i + 4) };
                    var pathStart = close + 4;
                    var pathMatch = /^[a-zA-Z_][a-zA-Z0-9_-]*(\.[a-zA-Z_][a-zA-Z0-9_-]*|\[\d+\])*/.exec(src.substring(pathStart));
                    if (!pathMatch) return { error: mkErr('Invalid or missing field path after ".$."', pathStart + 1) };
                    var refEnd = pathStart + pathMatch[0].length;
                    tokens.push({ type: 'FIELD_REF', value: src.substring(i, refEnd), col: col });
                    i = refEnd;
                    continue;
                }

                if (src[i] === '"' || src[i] === "'") {
                    var q = src[i], j = i + 1;
                    while (j < n) { if (src[j] === '\\') { j += 2; continue; } if (src[j] === q) break; j++; }
                    if (j >= n) return { error: mkErr('Unclosed string literal', col) };
                    tokens.push({ type: 'STRING', value: src.substring(i, j + 1), col: col });
                    i = j + 1;
                    continue;
                }

                // Member access on a call / parenthesised result — bmel:first({Op:S}.$.items).name.first —
                // or on the current element of an iterating function — bmel:item.type: ".path" reads that
                // field of the value.
                var _prevTok = tokens.length ? tokens[tokens.length - 1] : null;
                if (src[i] === '.' && _prevTok && (_prevTok.type === 'RPAREN'
                        || (_prevTok.type === 'IDENT' && _LOOP_REFS.indexOf(_prevTok.value) !== -1))) {
                    var memM = /^\.[a-zA-Z_][a-zA-Z0-9_-]*(?:\.[a-zA-Z_][a-zA-Z0-9_-]*)*/.exec(src.substring(i));
                    if (!memM) return { error: mkErr('Expected a field name after "."', col) };
                    tokens.push({ type: 'MEMBER', value: memM[0], col: col });
                    i += memM[0].length;
                    continue;
                }

                var numMatch = /^[0-9]+(\.[0-9]+)?/.exec(src.substring(i));
                if (numMatch) { tokens.push({ type: 'NUMBER', value: numMatch[0], col: col }); i += numMatch[0].length; continue; }

                var two = src.substring(i, i + 2);
                var twoMap = { '||': 'OR', '&&': 'AND', '==': 'EQ', '!=': 'NEQ', '<=': 'LTE', '>=': 'GTE' };
                if (twoMap[two]) { tokens.push({ type: twoMap[two], value: two, col: col }); i += 2; continue; }

                var oneMap = { '<':'LT','>':'GT','+':'PLUS','-':'MINUS','*':'STAR','/':'SLASH','!':'BANG','(':'LPAREN',')':'RPAREN',',':'COMMA','?':'QUESTION',':':'COLON' };
                if (oneMap[src[i]]) { tokens.push({ type: oneMap[src[i]], value: src[i], col: col }); i++; continue; }

                var identMatch = /^(?:bmel:[a-zA-Z_][a-zA-Z0-9_-]*|[a-zA-Z_][a-zA-Z0-9_]*)/.exec(src.substring(i));
                if (identMatch) {
                    var word = identMatch[0];
                    var ttype = word === 'true' ? 'TRUE' : word === 'false' ? 'FALSE' : word === 'null' ? 'NULL' : 'IDENT';
                    tokens.push({ type: ttype, value: word, col: col });
                    i += word.length;
                    continue;
                }

                // #{Name} — resource type / JSON field reference
                if (src[i] === '#' && src[i + 1] === '{') {
                    var _symClose = src.indexOf('}', i + 2);
                    var _nl = src.indexOf('\n', i + 2);
                    if (_symClose === -1 || (_nl !== -1 && _nl < _symClose)) return { error: mkErr('Unclosed "#{" — expected #{Name}', col, 2) };
                    var _symName = src.substring(i + 2, _symClose).trim();
                    if (!_symName) return { error: mkErr('Empty #{} reference — expected a resource type or field name', col, _symClose - i + 1) };
                    tokens.push({ type: 'HASH_REF', value: src.substring(i, _symClose + 1), name: _symName, col: col });
                    i = _symClose + 1;
                    continue;
                }

                // #enum.VALUE — enum constant reference
                if (src[i] === '#') {
                    var _enumM = /^#enum\.([a-zA-Z_][a-zA-Z0-9_]*)/.exec(src.substring(i));
                    if (_enumM) {
                        tokens.push({ type: 'ENUM_REF', value: _enumM[0], enumValue: _enumM[1], col: col });
                        i += _enumM[0].length;
                        continue;
                    }
                    return { error: mkErr('"#" must start #enum.VALUE or a #{Name} reference', col) };
                }

                return { error: mkErr('Unexpected character "' + src[i] + '"', col) };
            }

            tokens.push({ type: 'EOF', value: '(end)', col: n + 1 });
            return { tokens: tokens, error: null };
        }

        function _parse(tokens, knownFns) {
            var pos = 0;
            var allFns = knownFns.concat(_BUILTIN_FN_NAMES.filter(function(n) { return knownFns.indexOf(n) === -1; }));

            function peek()     { return tokens[pos] || { type: 'EOF', value: '', col: 0 }; }
            function advance()  { return tokens[pos++]; }
            function check(t)   { return peek().type === t; }
            function match(t)   { if (check(t)) { advance(); return true; } return false; }
            function peekNext() { return tokens[pos + 1] || { type: 'EOF', value: '', col: 0 }; }

            function mkErr(msg, tok) {
                tok = tok || peek();
                var e = new Error(msg);
                e.col = tok.col || 1;
                e.len = (tok.value && tok.value !== '(end)') ? tok.value.length : 1;
                return e;
            }

            function expect(type, msg) {
                if (!check(type)) throw mkErr(msg || ('Expected "' + type + '" but found "' + peek().value + '"'));
                return advance();
            }

            function parseExpr()   { return parseTernary(); }

            function parseTernary() {
                var left = parseDefault();
                if (check('QUESTION')) {
                    advance(); parseExpr();
                    expect('COLON', 'Expected ":" after "?" in ternary expression');
                    parseExpr();
                    return { type: 'ternary' };
                }
                return left;
            }

            function parseDefault() {
                var left = parseOr();
                while (check('IDENT') && peek().value === 'default' && peekNext().type !== 'LPAREN') { advance(); parseOr(); left = { type: 'binary' }; }
                return left;
            }

            function parseOr()           { var l = parseAnd();          while (check('OR'))               { advance(); parseAnd();           l = {type:'binary'}; } return l; }
            function parseAnd()          { var l = parseEquality();      while (check('AND'))              { advance(); parseEquality();       l = {type:'binary'}; } return l; }
            function parseEquality()     { var l = parseRelational();    while (check('EQ')||check('NEQ')) { advance(); parseRelational();     l = {type:'binary'}; } return l; }
            function parseRelational()   { var l = parseAdditive();      while (check('LTE')||check('GTE')||check('LT')||check('GT')) { advance(); parseAdditive();      l = {type:'binary'}; } return l; }
            function parseAdditive()     { var l = parseMultiplicative();while (check('PLUS')||check('MINUS'))  { advance(); parseMultiplicative(); l = {type:'binary'}; } return l; }
            function parseMultiplicative(){ var l = parseUnary();        while (check('STAR')||check('SLASH'))  { advance(); parseUnary();          l = {type:'binary'}; } return l; }

            function parseUnary() {
                if (check('BANG') || check('MINUS')) { advance(); parseUnary(); return { type: 'unary' }; }
                var node = parsePrimary();
                while (check('MEMBER')) { advance(); node = { type: 'member' }; }
                return node;
            }

            function parsePrimary() {
                var tok = peek();
                if (check('LPAREN'))  { advance(); parseExpr(); expect('RPAREN', 'Expected closing ")"'); return { type: 'group' }; }
                if (check('FIELD_REF')) { advance(); return { type: 'field_ref' }; }
                if (check('ENUM_REF'))  { advance(); return { type: 'enum_ref'  }; }
                if (check('HASH_REF'))  { advance(); return { type: 'hash_ref'  }; }
                if (check('STRING') || check('NUMBER') || check('TRUE') || check('FALSE') || check('NULL')) { advance(); return { type: 'literal' }; }
                if (check('IDENT')) {
                    var name = tok.value;
                    // Loop references inside an iterating function's body:
                    // bmel:item (current element of bmel:map) and bmel:i (index).
                    if (_LOOP_REFS.indexOf(name) !== -1 && peekNext().type !== 'LPAREN') { advance(); return { type: 'loop_ref' }; }
                    if (peekNext().type !== 'LPAREN') {
                        var hint = allFns.indexOf(name) !== -1 ? ' — did you mean ' + name + '(...)?' : '';
                        throw mkErr('"' + name + '" is not a valid expression' + hint, tok);
                    }
                    if (allFns.indexOf(name) === -1 && !_renamedFn(name, allFns)) throw mkErr('Unknown function "' + name + '"', tok);
                    advance(); advance();
                    var argc = 0;
                    if (!check('RPAREN')) { parseExpr(); argc++; while (match('COMMA')) { parseExpr(); argc++; } }
                    expect('RPAREN', 'Expected ")" after arguments of "' + name + '"');
                    var arity = _FN_ARITY[name];
                    if (arity) {
                        if (argc < arity[0]) throw mkErr('"' + name + '" requires at least ' + arity[0] + ' argument(s), got ' + argc, tok);
                        if (arity[1] !== -1 && argc > arity[1]) throw mkErr('"' + name + '" accepts at most ' + arity[1] + ' argument(s), got ' + argc, tok);
                    } else if (argc === 0 && knownFns.indexOf(name) === -1) {
                        throw mkErr('"' + name + '" requires at least 1 argument', tok);
                    }
                    return { type: 'func_call' };
                }
                if (tok.type === 'EOF') throw mkErr('Unexpected end of expression — expression is incomplete');
                throw mkErr('Expected an expression but found "' + tok.value + '"', tok);
            }

            try {
                parseExpr();
                var remaining = peek();
                if (remaining.type !== 'EOF') {
                    return { message: 'Unexpected "' + remaining.value + '" — expected end of expression or an operator', line: 1, col: remaining.col, length: (remaining.value && remaining.value !== '(end)') ? remaining.value.length : 1, severity: 'error' };
                }
                return null;
            } catch (e) {
                return { message: e.message, line: 1, col: e.col || 1, length: e.len || 1, severity: 'error' };
            }
        }

        // Schema-only validation also runs before opening the mapping composer, without
        // waiting for Monaco or the remote function catalog (published-event forks).
        svc.resourceReferenceErrors = function (expression, resourceTypes) {
            var tr = _tokenize(String(expression || ''));
            if (tr.error) { return []; }
            var functions = {};
            functions['bmel:get-resource-by-id'] = { name: 'bmel:get-resource-by-id', returnType: 'bmel:json', arguments: [
                {position: 0, name: 'resourceType', type: 'bmel:resource-type'},
                {position: 1, name: 'id', type: 'bmel:any'}] };
            functions['bmel:get-json-field'] = { name: 'bmel:get-json-field', returnType: 'bmel:any', arguments: [
                {position: 0, name: 'object', type: 'bmel:any'},
                {position: 1, name: 'field', type: 'bmel:json-field'}] };
            return _typeCheckExpression(tr.tokens.slice(), functions, {}, resourceTypes || []).errors
                .filter(function (e) { return /^(Unknown resource type|Unknown field)/.test(e.message); });
        };

        function validateExpression(expression, availableSources, expectedBmelType, expectedTypeLabel, resourceTypes) {
            var errors = [];
            if (!expression || !expression.trim()) return errors;

            var tr = _tokenize(expression.trim());
            if (tr.error) { errors.push(tr.error); return errors; }

            var parseErr = _parse(tr.tokens, KNOWN_FN_NAMES.slice());
            if (parseErr) { errors.push(parseErr); return errors; }

            var fnsByName = {};
            BMEL_FUNCTIONS.forEach(function (fn) { fnsByName[fn.name] = fn; });

            var srcByRef = {};
            (availableSources || []).forEach(function (src) {
                if (src.ref) srcByRef[src.ref] = src;
            });

            // null = no template context: #{…} names are not checked against it.
            var typeResult = _typeCheckExpression(tr.tokens.slice(), fnsByName, srcByRef,
                resourceTypes || null);
            typeResult.errors.forEach(function (e) { errors.push(e); });

            if (availableSources && availableSources.length) {
                var knownRefs = availableSources.map(function (s) { return s.ref; });
                var rfr = /\{[^}]+\}\.\$(?:\.[a-zA-Z0-9_.\[\]-]*)?/g, rfm;
                while ((rfm = rfr.exec(expression)) !== null) {
                    if (knownRefs.indexOf(rfm[0]) === -1) {
                        errors.push({ message: rfm[0] + ': unrecognised field ref', line: 1, col: rfm.index + 1, length: rfm[0].length, severity: 'error' });
                    }
                }
            }

            // Cross-type check — only when both sides are deterministic
            if (expectedBmelType && expectedBmelType !== 'bmel:any'
                    && typeResult.resultType && typeResult.resultType !== 'bmel:any'
                    && !_typesCompatible(typeResult.resultType, expectedBmelType)) {
                errors.push({
                    message:  'Type mismatch: expression returns ' + typeResult.resultType
                              + ' but component expects ' + expectedBmelType,
                    line: 1, col: 1,
                    length:   expression.trim().length,
                    severity: 'error'
                });
            }

            return errors;
        }

        // ── Type helpers exposed on svc ────────────────────────────────────

        var _BMEL_TYPE_LABELS = {
            'bmel:number':   'Number',   'bmel:integer':  'Integer',
            'bmel:string':   'String',   'bmel:boolean':  'Boolean',
            'bmel:date':     'Date',     'bmel:datetime': 'DateTime',
            'bmel:time':     'Time',     'bmel:array':    'Array',
            'bmel:any':      'Any',
            'bmel:enum':     'Enum',
            'bmel:json':     'JSON'
        };

        function bmelTypeLabel(bmelType) {
            return _BMEL_TYPE_LABELS[bmelType] || bmelType || 'Any';
        }

        // Maps frame component type + scalar_attribute_type → expected BMEL return type.
        function frameTypeToExpectedBmel(componentType, scalarAttributeType) {
            var ct  = (componentType        || '').toUpperCase();
            var sat = (scalarAttributeType  || '').toUpperCase();
            if (ct  === 'NESTED_TABLE') { return 'bmel:array'; }
            // ENUM is not a native BMEL type — its values are always strings
            if (sat === 'ENUM')         { return 'bmel:enum'; }
            if (scalarAttributeType)    { return _normSrcType(scalarAttributeType); }
            return 'bmel:any';
        }

        function _typesCompatible(argType, expectedType) {
            if (!expectedType || !argType)                              return true;
            if (expectedType === 'bmel:any')                           return true;
            if (expectedType === 'bmel:expression')                    return true;
            if (argType      === 'bmel:any')                           return true;

            var exp = expectedType.split('|').map(function (t) { return t.trim(); });
            var arg = argType.split('|').map(function (t) { return t.trim(); });

            return arg.some(function (a) {
                return exp.some(function (e) { return _singleTypeCompat(a, e); });
            });
        }

        function _singleTypeCompat(a, e) {
            if (!a || !e || a === e)                        return true;
            if (e === 'bmel:any' || a === 'bmel:any')      return true;
            // bmel:enum is only compatible with bmel:enum (use #enum.<VALUE> operator)
            if (a === 'bmel:enum' && e === 'bmel:enum')   return true;
            var numeric = { 'bmel:integer': true, 'bmel:number': true };
            if (numeric[a] && numeric[e])                  return true;
            if (e === 'bmel:array' && a.indexOf('bmel:array') === 0) return true;
            if (a === 'bmel:array' && e.indexOf('bmel:array') === 0) return true;
            var aArr = /^bmel:array<(.+)>$/.exec(a);
            var eArr = /^bmel:array<(.+)>$/.exec(e);
            if (aArr && eArr) {
                var aElem = aArr[1].indexOf('bmel:') === 0 ? aArr[1] : _normSrcType(aArr[1]);
                var eElem = eArr[1].indexOf('bmel:') === 0 ? eArr[1] : _normSrcType(eArr[1]);
                return _singleTypeCompat(aElem, eElem);
            }
            return false;
        }

        var _SWAGGER_TO_BMEL = {
            'string':'bmel:string','integer':'bmel:integer','number':'bmel:number','boolean':'bmel:boolean',
            'bool':'bmel:boolean','array':'bmel:array','object':'bmel:any','null':'bmel:any','undefined':'bmel:any',
            'int':'bmel:integer','int32':'bmel:integer','int64':'bmel:integer','long':'bmel:integer',
            'short':'bmel:integer','byte':'bmel:integer','float':'bmel:number','double':'bmel:number',
            'decimal':'bmel:number','bigdecimal':'bmel:number','date':'bmel:date','datetime':'bmel:datetime',
            'date-time':'bmel:datetime','date_time':'bmel:datetime','timestamp':'bmel:datetime','time':'bmel:time',
            'partial-time':'bmel:time','duration':'bmel:string','uuid':'bmel:string','guid':'bmel:string',
            'binary':'bmel:string','base64':'bmel:string','base64url':'bmel:string','password':'bmel:string',
            'email':'bmel:string','uri':'bmel:string','uri-reference':'bmel:string','uri-template':'bmel:string',
            'url':'bmel:string','hostname':'bmel:string','ipv4':'bmel:string','ipv6':'bmel:string',
            'idn-email':'bmel:string','idn-hostname':'bmel:string','iri':'bmel:string',
            'iri-reference':'bmel:string','json-pointer':'bmel:string','relative-json-pointer':'bmel:string',
            'regex':'bmel:string','html':'bmel:string','markdown':'bmel:string','json':'bmel:string',
            'xml':'bmel:string','csv':'bmel:string','phone':'bmel:string','currency':'bmel:string',
            'country':'bmel:string','language':'bmel:string','locale':'bmel:string','semver':'bmel:string',
            'ssn':'bmel:string','zip':'bmel:string',
            'enum':'bmel:enum','enumeration':'bmel:enum'
        };

        function _normSrcType(rawType) {
            if (!rawType) return 'bmel:any';
            var t = rawType.trim();
            if (t.indexOf('bmel:') === 0) return t;
            if (t.indexOf('column:') === 0) {
                var elemType = _normSrcType(t.substring(7));
                return 'bmel:array<' + elemType + '>';
            }
            var tl = t.toLowerCase();
            var arrayMatch = /^(?:array|list|set)\s*[<\(]\s*(.+?)\s*[>\)]\s*$/.exec(tl);
            if (arrayMatch) return 'bmel:array<' + _normSrcType(arrayMatch[1]) + '>';
            if (_SWAGGER_TO_BMEL[tl]) return _SWAGGER_TO_BMEL[tl];
            if (tl.indexOf('date-time') !== -1 || tl.indexOf('datetime') !== -1) return 'bmel:datetime';
            if (tl.indexOf('date')   !== -1) return 'bmel:date';
            if (tl.indexOf('time')   !== -1) return 'bmel:time';
            if (tl.indexOf('int')    !== -1) return 'bmel:integer';
            if (tl.indexOf('long')   !== -1) return 'bmel:integer';
            if (tl.indexOf('float')  !== -1 || tl.indexOf('double') !== -1 || tl.indexOf('decimal') !== -1) return 'bmel:number';
            if (tl.indexOf('bool')   !== -1) return 'bmel:boolean';
            if (tl.indexOf('array')  !== -1 || tl.indexOf('list') !== -1) return 'bmel:array';
            if (tl.indexOf('object') !== -1 || tl.indexOf('map')  !== -1) return 'bmel:any';
            // ENUM is a first-class BMEL type — use the #enum operator
            if (tl === 'enum' || tl === 'enumeration' || tl.indexOf('enum') === 0) return 'bmel:enum';
            return 'bmel:string';
        }

        function _stripBmel(s) { return (s || '').replace(/^bmel:/, ''); }

        // Function names are kebab-case (bmel:to-number). The former camelCase names
        // (bmel:toNumber) are still accepted as aliases, with a warning naming the new one.
        function _kebabFnName(name) {
            return 'bmel:' + _stripBmel(name)
                .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
                .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
                .toLowerCase();
        }
        function _renamedFn(name, knownFns) {
            if (!/^bmel:/.test(name || '') || !/[A-Z]/.test(name)) return null;
            var kebab = _kebabFnName(name);
            return knownFns.indexOf(kebab) !== -1 ? kebab : null;
        }

        // Special references valid inside the body of an iterating function
        // (bmel:map → bmel:item / bmel:i, bmel:for-range → bmel:i).
        var _LOOP_REFS = ['bmel:item', 'bmel:i'];

        // Functions whose bmel:any params legitimately take an array of the N
        // invocations of a MANY operation as a whole (pass-through / null
        // handling / size). bmel:json-* are allowed too (any JSON value).
        // bmel:switch is NOT here: its subject / case values are compared by equality.
        var _OCCURRENCES_PASSTHROUGH_FNS = ['bmel:self', 'bmel:nvl', 'bmel:coalesce', 'bmel:if', 'bmel:sizeof'];
        // Pass-through functions whose result is still the array of invocations
        // when one of their value arguments is — so it can't be laundered into a
        // scalar function, e.g. bmel:token-count(bmel:nvl({Op:X[]}.$.f, "")).
        var _OCCURRENCES_PROPAGATING_FNS = ['bmel:nvl', 'bmel:coalesce', 'bmel:if'];

        function _typeCheckExpression(tokens, fnsByName, srcByRef, resourceTypes) {
            var pos = 0;
            var errors = [];

            function peek()     { return tokens[pos]     || { type: 'EOF', value: '', col: 0 }; }
            function advance()  { return tokens[pos++]; }
            function check(t)   { return peek().type === t; }
            function match(t)   { if (check(t)) { advance(); return true; } return false; }
            function peekNext() { return tokens[pos + 1] || { type: 'EOF', value: '', col: 0 }; }

            var ANY = { valueType: 'bmel:any' };

            // ── Arrays of invocations (cardinality MANY) under scalar operators ───────
            // Comparison / logical operators and the ternary condition need ONE value;
            // an array of the N invocations of a MANY operation would be compared or
            // tested as a whole. Arithmetic already rejects arrays via _isNumericType.
            function _occOperandCheck(opTok, node) {
                if (node && node.occurrences) {
                    errors.push({ message: 'Operator "' + opTok.value + '" expects a single value, got an array of invocations ('
                        + (node.valueType || 'array') + ', cardinality MANY) — aggregate it first (e.g. bmel:sum, bmel:first, bmel:last)',
                        line: 1, col: opTok.col || 1, length: (opTok.value || '').length || 1, severity: 'error' });
                }
            }
            function _binaryBool(next, types) {
                var r = next();
                while (types.some(function (t) { return check(t); })) {
                    var opTok = peek(); advance();
                    var right = next();
                    _occOperandCheck(opTok, r);
                    _occOperandCheck(opTok, right);
                    r = { valueType: 'bmel:boolean' };
                }
                return r;
            }

            // #{…} arguments: allowed only for bmel:resource-type / bmel:json-field params, which
            // accept nothing else; names are checked against the template when it's known.
            // Returns true when the argument was handled (no generic type check needed).
            function _checkSymbolArg(fnName, nameTok, i, param, argNode, argNodes) {
                var symbolType = _symbolParamType(param.type);
                var hashTok    = argNode && argNode.hashRef;
                var argLabel   = fnName + '(): argument ' + (i + 1) + ' \'' + _stripBmel(param.name) + '\'';
                function err(msg, tok) {
                    errors.push({ message: msg, line: 1, col: (tok || nameTok).col || 1,
                        length: ((tok || nameTok).value || fnName).length, severity: 'error' });
                }
                if (!symbolType) {
                    if (hashTok) {
                        err(argLabel + ' does not take a #{…} reference — #{Name} names a resource type '
                            + '(bmel:get-resource-by-id) or a JSON field (bmel:get-json-field)', hashTok);
                        return true;
                    }
                    return false;
                }
                if (!hashTok) {
                    err(argLabel + ' expects a ' + (symbolType === 'bmel:resource-type' ? '#{ResourceType}' : '#{FIELD_NAME}')
                        + ' reference, got ' + ((argNode && argNode.valueType) || 'bmel:any'));
                    return true;
                }
                if (symbolType === 'bmel:resource-type' && resourceTypes) {
                    var rtFound = _findByName(resourceTypes, hashTok.name);
                    if (!rtFound.item) {
                        err('Unknown resource type "' + hashTok.name + '" — not a resource type of this template'
                            + (rtFound.hint ? ' (did you mean #{' + rtFound.hint + '}?)' : ''), hashTok);
                    }
                }
                if (symbolType === 'bmel:json-field') {
                    var objRt = argNodes[0] && argNodes[0].resourceType;
                    if (objRt && objRt.fields) {
                        var fFound = _findByName(objRt.fields, hashTok.name);
                        if (!fFound.item) {
                            err('Unknown field "' + hashTok.name + '" for resource type ' + objRt.name
                                + (fFound.hint ? ' (did you mean #{' + fFound.hint + '}?)' : ''), hashTok);
                        }
                    }
                }
                return true;
            }

            function parseExpr() { return parseTernary(); }
            function parseTernary() { var left = parseDefault(); if (check('QUESTION')) { _occOperandCheck(peek(), left); advance(); parseExpr(); if (match('COLON')) parseExpr(); } return left; }
            // "x default y" is null-handling pass-through — an array of invocations stays one.
            function parseDefault() { var r = parseOr(); while (check('IDENT') && peek().value === 'default' && peekNext().type !== 'LPAREN') { advance(); var d = parseOr(); r = { valueType: 'bmel:any', occurrences: !!(r.occurrences || (d && d.occurrences)) }; } return r; }
            function parseOr()         { return _binaryBool(parseAnd,        ['OR']); }
            function parseAnd()        { return _binaryBool(parseEquality,   ['AND']); }
            function parseEquality()   { return _binaryBool(parseRelational, ['EQ', 'NEQ']); }
            function parseRelational() { return _binaryBool(parseAdditive,   ['LTE', 'GTE', 'LT', 'GT']); }
            // ── Arithmetic type helpers ──────────────────────────────────────────────
            // bmel:any is allowed because the field type may be unknown at parse time.
            function _isNumericType(t) { return t === 'bmel:number' || t === 'bmel:integer' || t === 'bmel:any'; }
            function _typeLabel(t) { return t ? t.replace(/^bmel:/, '') : 'unknown'; }
            function _arithError(opTok, side, actualType) {
                errors.push({ message: 'Type error: operator "' + opTok.value + '" requires numeric operands — '
                    + side + ' operand is ' + _typeLabel(actualType) + ', expected number or integer',
                    line: 1, col: opTok.col, length: 1, severity: 'error' });
            }
            function parseAdditive() {
                var r = parseMultiplicative();
                while (check('PLUS') || check('MINUS')) {
                    var opTok = peek(); advance();
                    var right = parseMultiplicative();
                    if (!_isNumericType(r.valueType))     _arithError(opTok, 'left',  r.valueType);
                    if (!_isNumericType(right.valueType)) _arithError(opTok, 'right', right.valueType);
                    r = { valueType: 'bmel:number' };
                }
                return r;
            }
            function parseMultiplicative() {
                var r = parseUnary();
                while (check('STAR') || check('SLASH')) {
                    var opTok = peek(); advance();
                    var right = parseUnary();
                    if (!_isNumericType(r.valueType))     _arithError(opTok, 'left',  r.valueType);
                    if (!_isNumericType(right.valueType)) _arithError(opTok, 'right', right.valueType);
                    r = { valueType: 'bmel:number' };
                }
                return r;
            }
            function parseUnary() { if (check('BANG'))  { var bangTok = peek(); advance(); _occOperandCheck(bangTok, parseUnary()); return { valueType: 'bmel:boolean' }; } if (check('MINUS')) { advance(); var operand = parseUnary(); if (!_isNumericType(operand.valueType)) { errors.push({ message: 'Type error: unary "-" requires a numeric operand, got ' + _typeLabel(operand.valueType), line: 1, col: (peek().col || 1), length: 1, severity: 'error' }); } return { valueType: 'bmel:number' }; } return _withMembers(parsePrimary()); }

            // ".path" after a call / group. On the element bmel:first / bmel:last took from an
            // array ref, it's that field of the array's items: <array ref>.path, typed as one
            // item's value. Anything else can't be resolved statically → bmel:any.
            // The member keeps its path as ref ({Op:S}.$.items.sub), so an outer bmel:first over
            // it resolves the next level the same way — any depth of nested arrays.
            function _withMembers(node) {
                while (check('MEMBER')) {
                    var mem = advance();
                    var ref = node && node.elementOf ? node.elementOf + mem.value : null;
                    var child = ref ? srcByRef[ref] : null;
                    var t = child ? _normSrcType(child.valueType) : 'bmel:any';
                    // One element per array level: a path crossing another array on its way
                    // (….tool_calls.id with no bmel:first on tool_calls) is still a list.
                    var segs = mem.value.replace(/^\./, '').split('.');
                    var crosses = false;
                    for (var k = 1; ref && k < segs.length; k++) {
                        var mid = srcByRef[node.elementOf + '.' + segs.slice(0, k).join('.')];
                        var vt = mid && mid.valueType || '';
                        if (vt === 'array' || /:array$/.test(vt)) { crosses = true; }
                    }
                    var item = /^bmel:array<(.+)>$/.exec(t);
                    node = { valueType: item && !crosses ? item[1] : t, ref: ref };
                }
                return node;
            }

            function parsePrimary() {
                var tok = peek();
                if (check('LPAREN'))    { advance(); var inner = parseExpr(); match('RPAREN'); return inner || ANY; }
                if (check('FIELD_REF')) { advance(); var src = srcByRef[tok.value]; return { valueType: src ? _normSrcType(src.valueType) : 'bmel:any', occurrences: !!(src && src.isOccurrences), ref: tok.value }; }
                if (check('ENUM_REF'))  { advance(); return { valueType: 'bmel:enum' }; }
                if (check('HASH_REF'))  { advance(); return { valueType: 'bmel:symbol', hashRef: tok }; }
                if (check('STRING'))    { advance(); return { valueType: 'bmel:string' }; }
                if (check('NUMBER'))    { advance(); return { valueType: (tok.value && tok.value.indexOf('.') === -1) ? 'bmel:integer' : 'bmel:number' }; }
                if (check('TRUE')||check('FALSE')) { advance(); return { valueType: 'bmel:boolean' }; }
                if (check('NULL'))      { advance(); return ANY; }
                if (check('IDENT')) {
                    var nameTok = tok;
                    var fnName  = tok.value;
                    if (peekNext().type !== 'LPAREN') { advance(); return ANY; }
                    var _newName = !fnsByName[fnName] && _renamedFn(fnName, Object.keys(fnsByName));
                    if (_newName) {
                        errors.push({ message: fnName + ' was renamed to ' + _newName + ' — the old name still works, but use the new one',
                            line: 1, col: nameTok.col || 1, length: fnName.length, severity: 'warning' });
                        fnName = _newName;
                    }
                    advance(); advance();
                    var fnDef    = fnsByName[fnName];
                    var argNodes = [];
                    if (!check('RPAREN')) { argNodes.push(parseExpr()); while (match('COMMA')) { argNodes.push(parseExpr()); } }
                    match('RPAREN');
                    if (fnDef && fnDef.arguments && fnDef.arguments.length > 0) {
                        var params     = fnDef.arguments;
                        var isVariadic = params[params.length - 1] && params[params.length - 1].variadic;
                        argNodes.forEach(function (argNode, i) {
                            var paramIdx = (isVariadic && i >= params.length - 1) ? params.length - 1 : i;
                            if (paramIdx >= params.length) return;
                            var param        = params[paramIdx];
                            var argType      = (argNode && argNode.valueType) || 'bmel:any';
                            var expectedType = param.type || 'bmel:any';
                            if (_checkSymbolArg(fnName, nameTok, i, param, argNode, argNodes)) {
                                return;
                            }
                            if (!_typesCompatible(argType, expectedType)) {
                                errors.push({ message: fnName + '(): argument ' + (i + 1) + ' \'' + _stripBmel(param.name) + '\'' + ' expects ' + expectedType + ', got ' + argType, line: 1, col: nameTok.col || 1, length: fnName.length, severity: 'error' });
                            } else if (argNode && argNode.occurrences
                                    && expectedType.indexOf('bmel:array') === -1
                                    && expectedType !== 'bmel:expression'
                                    && _OCCURRENCES_PASSTHROUGH_FNS.indexOf(fnName) === -1
                                    && fnName.indexOf('bmel:json-') !== 0) {
                                // A bmel:any param (or a scalar one fed a bmel:any result, e.g.
                                // bmel:upper(bmel:coalesce(...))) would accept anything — but an
                                // array of the N invocations of a MANY operation is not "a value":
                                // a scalar function (tokenCount, toTimestamp, toString…) would
                                // silently run once over the whole array. Require aggregating first.
                                errors.push({ message: fnName + '(): argument ' + (i + 1) + ' \'' + _stripBmel(param.name) + '\''
                                    + ' expects a single value, got an array of invocations (' + argType
                                    + ', cardinality MANY) — aggregate it first (e.g. bmel:sum, bmel:first, bmel:last)',
                                    line: 1, col: nameTok.col || 1, length: fnName.length, severity: 'error' });
                            }
                        });
                    }

                    var _arg0Type = (argNodes[0] && argNodes[0].valueType) || 'bmel:any';

                    // bmel:self(x) → type of x (still an array of invocations if x was)
                    if (fnName === 'bmel:self' && argNodes.length === 1) {
                        return { valueType: _arg0Type, occurrences: !!argNodes[0].occurrences, resourceType: argNodes[0].resourceType };
                    }

                    // bmel:get-resource-by-id(#{Type}, id) → the resource JSON; remembers its type
                    // so bmel:get-json-field can resolve (and type) the field.
                    if (fnName === 'bmel:get-resource-by-id') {
                        var _rtTok = argNodes[0] && argNodes[0].hashRef;
                        var _rt = _rtTok && resourceTypes ? _findByName(resourceTypes, _rtTok.name).item : null;
                        return { valueType: 'bmel:json', resourceType: _rt };
                    }

                    // bmel:get-json-field(obj, #{FIELD}) → the field's column type when known
                    if (fnName === 'bmel:get-json-field') {
                        var _objRt = argNodes[0] && argNodes[0].resourceType;
                        var _fTok  = argNodes[1] && argNodes[1].hashRef;
                        var _fld   = _objRt && _fTok ? _findByName(_objRt.fields, _fTok.name).item : null;
                        return { valueType: _fieldValueType(_fld) };
                    }

                    // bmel:filter(array, predicate) → the same array type, fewer elements
                    if (fnName === 'bmel:filter' && argNodes.length === 2) {
                        return { valueType: _arg0Type, occurrences: !!argNodes[0].occurrences };
                    }

                    // bmel:first / bmel:last: bmel:array<T> → T
                    if ((fnName === 'bmel:first' || fnName === 'bmel:last') && argNodes.length === 1) {
                        var _arrMatch = /^bmel:array<(.+)>$/.exec(_arg0Type);
                        // elementOf: the array ref — a member access (.message.content) on
                        // this element is typed as that field of the array's items.
                        return { valueType: _arrMatch ? _arrMatch[1] : 'bmel:any', elementOf: argNodes[0].ref || null };
                    }

                    // bmel:switch — infer result type from value branches
                    if (fnName === 'bmel:switch' && argNodes.length >= 3) {
                        var _swResIdx = [];
                        for (var _si = 2; _si < argNodes.length; _si += 2) { _swResIdx.push(_si); }
                        if (argNodes.length % 2 === 0) { _swResIdx.push(argNodes.length - 1); }

                        var _swTypes = [];
                        _swResIdx.forEach(function (idx) {
                            var t = (argNodes[idx] && argNodes[idx].valueType) || 'bmel:any';
                            if (t !== 'bmel:any' && _swTypes.indexOf(t) === -1) { _swTypes.push(t); }
                        });

                        if (_swTypes.length === 1) {
                            return { valueType: _swTypes[0] };
                        }
                        var _allNum = _swTypes.length > 0 && _swTypes.every(function (t) {
                            return t === 'bmel:integer' || t === 'bmel:number';
                        });
                        if (_allNum) { return { valueType: 'bmel:number' }; }
                        if (_swTypes.length > 1) {
                            errors.push({
                                message:  'bmel:switch(): inconsistent result types ['
                                          + _swTypes.join(', ')
                                          + '] — all case values must return the same type',
                                line: 1, col: nameTok.col || 1,
                                length:   fnName.length,
                                severity: 'error'
                            });
                        }
                        return ANY;
                    }

                    var _fnResult = { valueType: fnDef ? (fnDef.returnType || 'bmel:any') : 'bmel:any' };
                    if (_OCCURRENCES_PROPAGATING_FNS.indexOf(fnName) !== -1
                            && argNodes.some(function (a) { return a && a.occurrences; })) {
                        _fnResult.occurrences = true;
                    }
                    return _fnResult;
                }
                if (tok.type !== 'EOF') advance();
                return ANY;
            }

            var _rootResult = { valueType: 'bmel:any' };
            try { _rootResult = parseExpr() || _rootResult; } catch (e) { /* syntax already validated */ }
            if (_rootResult && _rootResult.hashRef) {
                errors.push({ message: _rootResult.hashRef.value + ' is not a value on its own — use it as an argument of '
                    + 'bmel:get-resource-by-id (resource type) or bmel:get-json-field (field name)',
                    line: 1, col: _rootResult.hashRef.col || 1, length: _rootResult.hashRef.value.length, severity: 'error' });
                _rootResult = { valueType: 'bmel:any' };
            }
            return { errors: errors, resultType: (_rootResult && _rootResult.valueType) || 'bmel:any' };
        }

        // ----------------------------------------------------------------
        //  5. Open the editor dialog
        // ----------------------------------------------------------------

        function openEditor(config) {
            return loadMonaco().then(function () {
                return $mdDialog.show({
                    templateUrl:         'profile/tabs/biz-events/bmel-editor/bmel-editor-dialog.html',
                    controller:          'BmelEditorDialogController',
                    controllerAs:        'ctrl',
                    bindToController:    true,
                    clickOutsideToClose: false,
                    escapeToClose:       false,
                    multiple: true,
                    locals: {
                        targetComponentId:   config.targetComponentId,
                        targetComponentName: config.targetComponentName,
                        currentExpression:   config.currentExpression   || '',
                        availableSources:    config.availableSources    || [],
                        expectedBmelType:    config.expectedBmelType    || null,
                        expectedTypeLabel:   config.expectedTypeLabel   || null,
                        enumValues:          config.enumValues          || [],
                        resourceTypes:       config.resourceTypes       || []
                    }
                });
            });
        }

    }]);